// @flow
import React from 'react';
import './Scheduler.scss';
import ParticipantList from '../participant-list/ParticipantList';
import CoachList from '../coach-list/CoachList';
import { DragDropContext } from 'react-beautiful-dnd';
import store from '../../general/store';
import { useSelector } from 'react-redux';
import type { State } from '../../general/types';
import { Alert, message } from 'antd';
import {
  beginBoard,
  fetchSchedule,
  scheduleSliceActions,
} from '../../slices/scheduleSlice';
import { isMasterDate, positiveId } from '../realtime/boardIdentity';
import { fetchSkills } from '../../slices/skillSlice';
import {
  deleteParticipant,
  moveParticipant,
  postParticipant,
  prePatchParticipant,
} from '../../slices/participantSlice';
import ScheduleHeader from '../schedule-header/ScheduleHeader';
import { MONDAY } from '../../utilities/masterDatesEnum';
import { locationRequests } from '../../slices/locationSlice';
import { ParticipantStatusTypesEnum } from '../../utilities/participantStatusTypesEnum';
import { getToday } from '../../utilities/getToday';
import AddParticipantScheduleTypesModal from './AddParticipantScheduleTypesModal';
import useScheduleRealtime from '../realtime/useScheduleRealtime';
import { scheduleSocket } from '../realtime/scheduleSocket';

function participantUserIdFromDraggable(participantId: string): ?number {
  if (participantId == null) return null;
  const raw = String(participantId);
  if (raw.includes('_')) {
    const n = parseInt(raw.split('_')[0], 10);
    return Number.isNaN(n) ? null : n;
  }
  // Fall back: look up Redux for user id
  const p = store.getState().participants.byId[participantId];
  if (p && p.user != null) {
    return typeof p.user === 'object' ? p.user.id : p.user;
  }
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? null : n;
}

export default function Scheduler({ isMaster }: { isMaster?: boolean }) {
  const scheduleError = useSelector((state: State) => state.schedule.error);
  const locksByParticipantUserId = useSelector(
    (state: State) => state.schedule.locksByParticipantUserId || {}
  );
  const [addModalState, setAddModalState] = React.useState({
    visible: false,
    participantId: null,
    coachScheduleId: null,
    coachUserId: null,
    currentScheduleTypeId: null,
    programScheduleTypes: [],
    date: null,
    isMaster: false,
  });
  const dragLockUserIdRef = React.useRef(null);
  // Promise of lock grant for the in-flight drag (must resolve true before PATCH)
  const dragLockGrantedRef = React.useRef(null);
  const intendedBoard = useSelector(
    (state: State) => state.schedule.intendedBoard
  );
  const scheduleRecordId = useSelector((state: State) => state.schedule.id);
  const [routeDate, setRouteDate] = React.useState(() =>
    isMaster
      ? localStorage.getItem('master_date') || MONDAY
      : localStorage.getItem('schedule_date') || getToday()
  );
  const storedProgramId = positiveId(localStorage.getItem('program'));
  const storedTypeId = positiveId(localStorage.getItem('scheduleType'));
  const adopted =
    intendedBoard && String(intendedBoard.date) === String(routeDate)
      ? intendedBoard
      : null;
  const routeBoard = {
    programId: (adopted && adopted.programId) || storedProgramId,
    date: routeDate,
    scheduleTypeId: (adopted && adopted.scheduleTypeId) || storedTypeId,
    scheduleId: adopted && scheduleRecordId != null ? scheduleRecordId : null,
    isMaster: !!isMaster,
  };

  // Live multi-admin feed. The route board is assigned during render, not from Redux date.
  useScheduleRealtime(routeBoard);

  React.useEffect(() => {
    store.dispatch(fetchSkills());
    store.dispatch(locationRequests.fetch());
  }, []);

  React.useLayoutEffect(() => {
    // Switch reuses this component when the route changes, so useState keeps
    // the previous mode's date. Pull the date for the mode we're on before
    // the mismatch check, or Master shows the schedule-unavailable alert.
    const modeDate = isMaster
      ? localStorage.getItem('master_date') || MONDAY
      : localStorage.getItem('schedule_date') || getToday();
    if (String(routeDate) !== String(modeDate)) {
      setRouteDate(modeDate);
      return;
    }
    if (!!isMaster !== isMasterDate(routeDate)) {
      store.dispatch(scheduleSliceActions.markBoardMismatch());
      return;
    }
    store.dispatch(
      beginBoard({
        programId: storedProgramId,
        date: routeDate,
        scheduleTypeId: storedTypeId,
        isMaster: !!isMaster,
      })
    );
    store.dispatch(
      fetchSchedule({
        program: storedProgramId,
        date: routeDate,
        scheduleType: storedTypeId,
      })
    );
    const epoch = store.getState().schedule.boardEpoch;
    return () => {
      // A newer Scheduler/Master mount has already called beginBoard.
      if (store.getState().schedule.boardEpoch === epoch) {
        store.dispatch(scheduleSliceActions.clearIntendedBoard());
      }
    };
    // Program and type adoption must not re-run this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMaster, routeDate]);

  const onDragStart = (start) => {
    let participantId = String(start.draggableId).replace('participant-', '');
    const userId = participantUserIdFromDraggable(participantId);
    if (userId == null) return;

    // Soft lock: if another admin already holds lock, mark denied (block on drop)
    const holder = locksByParticipantUserId[String(userId)];
    const me = store.getState().auth.currentUser;
    if (holder != null && me && holder !== me.id) {
      message.warning('This participant is being edited by another admin.');
      dragLockUserIdRef.current = null;
      dragLockGrantedRef.current = Promise.resolve(false);
      return;
    }

    dragLockUserIdRef.current = userId;
    // Wait for server grant — onDragEnd will refuse mutation if denied
    dragLockGrantedRef.current = scheduleSocket.requestLock(userId);
  };

  const onDragEnd = async (result) => {
    let heldUserId = dragLockUserIdRef.current;
    const lockPromise = dragLockGrantedRef.current;
    dragLockUserIdRef.current = null;
    dragLockGrantedRef.current = null;
    const release = () => {
      if (heldUserId != null) {
        scheduleSocket.releaseLock(heldUserId);
      }
    };
    try {
      if (!result.destination) {
        return;
      }
      let participantId = result.draggableId.replace('participant-', '');
      const sourceId = parseInt(result.source.droppableId.split('_')[1]);
      const destinationId = parseInt(
        result.destination.droppableId.split('_')[1]
      );
      if (result.source.droppableId === result.destination.droppableId) {
        return;
      }

      // Enforce soft lock: do not PATCH if we did not win the lock
      const userId =
        heldUserId != null
          ? heldUserId
          : participantUserIdFromDraggable(participantId);
      if (userId != null) {
        const remoteHolder =
          store.getState().schedule.locksByParticipantUserId &&
          store.getState().schedule.locksByParticipantUserId[String(userId)];
        const me = store.getState().auth.currentUser;
        if (remoteHolder != null && me && remoteHolder !== me.id) {
          message.warning('This participant is being edited by another admin.');
          return;
        }
        let granted = true;
        if (lockPromise) {
          granted = await lockPromise;
        } else {
          granted = await scheduleSocket.requestLock(userId);
        }
        if (!granted) {
          message.warning(
            'Could not edit this participant — another admin has it locked.'
          );
          return;
        }
        heldUserId = userId;
      }

      let status = null;
      // Add and remove off status when moving to and from the Participants Off list
      if (result.destination.droppableId.startsWith('participantDroppable')) {
        switch (result.destination.droppableId) {
          case 'participantDroppable_0':
            status = ParticipantStatusTypesEnum.SCHEDULED;
            break;
          case 'participantDroppableOff_0':
            status = ParticipantStatusTypesEnum.OFF;
            break;
          case 'participantDroppableOff_1':
            status = ParticipantStatusTypesEnum.CALLED_OUT;
            break;
          case 'participantDroppableOff_2':
            status = ParticipantStatusTypesEnum.NO_SHOW;
            break;
          default:
            status = ParticipantStatusTypesEnum.SCHEDULED;
        }
      } else if (
        result.source.droppableId.startsWith('participantDroppable') &&
        result.destination.droppableId.startsWith('coachDroppable')
      ) {
        status = ParticipantStatusTypesEnum.SCHEDULED;
      }

      if (
        result.source.droppableId.startsWith('participantDroppable') &&
        result.destination.droppableId.startsWith('coachDroppable')
      ) {
        const state = store.getState();
        const coachScheduleId = destinationId;
        const coach = state.coaches.byId[coachScheduleId];
        const coachUserId = coach && coach.user && coach.user.id;
        const currentScheduleTypeId =
          state.schedule.type && state.schedule.type.id;
        const programScheduleTypes = state.schedule.types || [];
        const date = isMaster
          ? localStorage.getItem('master_date') || MONDAY
          : localStorage.getItem('schedule_date') || getToday();

        // Skip the modal entirely when the program has only one schedule type
        // (or types haven't loaded yet) — preserves existing single-drop UX.
        if (programScheduleTypes.length <= 1 || !coachUserId) {
          await store.dispatch(
            postParticipant(participantId, coachScheduleId, status, isMaster)
          );
        } else {
          setAddModalState({
            visible: true,
            participantId,
            coachScheduleId,
            coachUserId,
            currentScheduleTypeId,
            programScheduleTypes,
            date,
            isMaster: !!isMaster,
          });
        }
      } else if (
        result.source.droppableId.startsWith('coachDroppable') &&
        result.destination.droppableId.startsWith('coachDroppable')
      ) {
        await store.dispatch(
          moveParticipant(participantId, sourceId, destinationId, isMaster)
        );
      } else if (
        result.source.droppableId.startsWith('coachDroppable') &&
        result.destination.droppableId.startsWith('participantDroppable')
      ) {
        await store.dispatch(
          deleteParticipant(participantId, sourceId, status, isMaster)
        );
      } else if (
        result.source.droppableId.startsWith('participantDroppable') &&
        result.destination.droppableId.startsWith('participantDroppable')
      ) {
        await store.dispatch(
          prePatchParticipant({
            id: participantId,
            status: status,
            isMaster: isMaster,
          })
        );
      }
    } finally {
      release();
    }
  };

  return (
    <div className="scheduler">
      <div className="sub-header">
        <ScheduleHeader isMaster={isMaster} onRouteDate={setRouteDate} />
      </div>
      {scheduleError ? (
        <Alert
          message="Schedule Not Available"
          description="No schedule types configured for this program. Please create schedule types in Admin settings or select a different program."
          type="warning"
          showIcon
          style={{ margin: '20px' }}
        />
      ) : (
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="left-side">
            <ParticipantList isMaster={isMaster} />
          </div>
          <div className="right-side">
            <CoachList isMaster={isMaster} />
          </div>
        </DragDropContext>
      )}
      {addModalState.visible && (
        <AddParticipantScheduleTypesModal
          visible={addModalState.visible}
          onClose={() => setAddModalState((s) => ({ ...s, visible: false }))}
          participantId={addModalState.participantId}
          coachScheduleId={addModalState.coachScheduleId}
          coachUserId={addModalState.coachUserId}
          currentScheduleTypeId={addModalState.currentScheduleTypeId}
          programScheduleTypes={addModalState.programScheduleTypes}
          date={addModalState.date}
          isMaster={addModalState.isMaster}
        />
      )}
    </div>
  );
}
